import type * as React from "react";

import { CalendarIcon } from "lucide-react";

import { invoke } from "../lib/utils";
import DatesRangePicker, {
  type DatesRangePickerOnChangeData,
} from "../pickers/dayPicker/DatesRangePicker";
import InputView from "../views/InputView";
import BaseInput, {
  type BaseInputProps,
  type BaseInputState,
  type DateRelatedProps,
  type MarkedValuesProps,
  type MinMaxValueProps,
  type RangeRelatedProps,
} from "./BaseInput";
import {
  buildValue,
  parseArrayOrValue,
  parseDatesRange,
  parseValue,
} from "./parse";

const DATES_SEPARATOR = " - ";

export type DatesRangeInputProps = BaseInputProps &
  DateRelatedProps &
  MarkedValuesProps &
  MinMaxValueProps &
  RangeRelatedProps;

export type DatesRangeInputOnChangeData = DatesRangeInputProps;

class DatesRangeInput extends BaseInput<DatesRangeInputProps, BaseInputState> {
  /**
   * Component responsibility:
   *  - parse input value (start: Moment, end: Moment)
   *  - handle DayPicker change (format {start: Moment, end: Moment} into
   *    string 'start - end')
   */
  public static readonly defaultProps = {
    ...BaseInput.defaultProps,
    dateFormat: "DD-MM-YYYY",
    icon: CalendarIcon,
  };

  constructor(props: DatesRangeInputProps) {
    super(props);
    this.state = { popupIsClosed: true };
  }

  public render() {
    const {
      value,
      dateFormat,
      initialDate,
      maxDate,
      minDate,
      closable,
      marked,
      markColor,
      localization,
      allowSameEndDate,
      ...rest
    } = this.props;

    return (
      <InputView
        popupIsClosed={this.state.popupIsClosed}
        {...(rest as any)}
        value={value}
        onMount={this.onInputViewMount}
        closePopup={this.closePopup}
        openPopup={this.openPopup}
        renderPicker={this.getPicker}
      />
    );
  }

  private getPicker = () => {
    const {
      value,
      dateFormat,
      markColor,
      marked,
      initialDate,
      localization,
      minDate,
      maxDate,
      tabIndex,
      pickerWidth,
      pickerStyle,
      allowSameEndDate,
    } = this.props;
    const { start, end } = parseDatesRange(value as string, dateFormat);

    const markedParsed = parseArrayOrValue(marked, dateFormat, localization);
    const minDateParsed = parseValue(minDate, dateFormat, localization);
    const maxDateParsed = parseValue(maxDate, dateFormat, localization);

    let initializeWith;

    if ((!initialDate && minDateParsed) || maxDateParsed) {
      initializeWith = minDateParsed || maxDateParsed;
    } else {
      initializeWith = buildValue(start, initialDate, localization, dateFormat);
    }

    return (
      <DatesRangePicker
        isPickerInFocus={this.isPickerInFocus}
        isTriggerInFocus={this.isTriggerInFocus}
        inline={this.props.inline}
        onCalendarViewMount={this.onCalendarViewMount}
        closePopup={this.closePopup}
        onChange={this.handleSelect}
        dateFormat={dateFormat!}
        initializeWith={initializeWith!}
        start={start}
        end={end}
        marked={markedParsed}
        markColor={markColor}
        minDate={parseValue(minDate, dateFormat, localization)}
        maxDate={parseValue(maxDate, dateFormat, localization)}
        localization={localization}
        onHeaderClick={() => undefined}
        tabIndex={tabIndex}
        pickerWidth={pickerWidth}
        pickerStyle={pickerStyle}
        allowSameEndDate={allowSameEndDate}
      />
    );
  };

  private handleSelect = (
    e: React.SyntheticEvent<HTMLElement> | null,
    { value }: DatesRangePickerOnChangeData,
  ) => {
    const { dateFormat } = this.props;
    const { start, end } = value;
    let outputString = "";
    if (start && end) {
      outputString = `${start.format(dateFormat!)}${DATES_SEPARATOR}${end.format(dateFormat!)}`;
    } else if (start) {
      outputString = `${start.format(dateFormat!)}${DATES_SEPARATOR}`;
    }
    invoke(this.props, "onChange", e, { ...this.props, value: outputString });
    if (this.props.closable && start && end) {
      this.closePopup();
    }
  };
}

export default DatesRangeInput;
